import { useEffect, useState, useMemo } from 'react';
import { 
    MdRefresh, 
    MdCheckCircle, 
    MdWarning,
    MdFilterList,
    MdSpeed,
    MdDataUsage,
    MdAttachMoney,
    MdAccessTime
} from 'react-icons/md';
import { 
    Card, 
    CardContent, 
    Typography, 
    Grid, 
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    LinearProgress,
    TextField,
    Button,
    Tabs,
    Tab,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Badge
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip as RechartsTooltip, 
    Legend, 
    ResponsiveContainer, 
    PieChart, 
    Pie, 
    Cell,
    BarChart,
    Bar,
    AreaChart,
    Area,
} from 'recharts';
import { ExpandMore } from '@mui/icons-material';
import apiAdmin from '../../services/api-admin';
import './tpm-analytics.scss';
import { FormatMoney } from '../../services/utilities';

const COLORS = {
    success: '#4caf50',
    error: '#f44336',
    warning: '#ff9800',
    info: '#2196f3',
    primary: '#1976d2',
    secondary: '#9c27b0',
    accent: '#00bcd4'
};

const TpmAnalytics = () => {
    
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);    
    const [dateRange, setDateRange] = useState({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [selectedCounties, setSelectedCounties] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [availableCounties, setAvailableCounties] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    
    const [dashboardSummary, setDashboardSummary] = useState(null);
    const [countyPerformance, setCountyPerformance] = useState([]);

    const [tokenTrends, setTokenTrends] = useState([]);
    const [throughputMetrics, setThroughputMetrics] = useState([]);
    const [processingTimeStats, setProcessingTimeStats] = useState([]);
    const [documentDistribution, setDocumentDistribution] = useState([]);
    const [batchDurations, setBatchDurations] = useState([]);
    const [costAnalysis, setCostAnalysis] = useState([]);
    
    const [timeInterval, setTimeInterval] = useState('Day');
    const [groupByDimension, setGroupByDimension] = useState('County');

    const getFilterObject = useMemo(() => {
        const filter = {
            startDate: dateRange.startDate || null,
            endDate: dateRange.endDate || null,
            counties: selectedCounties.length > 0 ? selectedCounties : null,
            categories: selectedCategories.length > 0 ? selectedCategories : null
        };

        return filter;
    }, [dateRange, selectedCounties, selectedCategories]);

    const fetchDashboardSummary = async () => {
        try {
            const response = await apiAdmin.post('/tpm-analytics/dashboard-summary', getFilterObject);
            setDashboardSummary(response.data);
        } catch (err) {
            console.error('Error fetching dashboard summary:', err);
        }
    };

    const fetchCountyPerformance = async () => {
        try {
            const response = await apiAdmin.post('/tpm-analytics/county-performance', getFilterObject);
            setCountyPerformance(response.data);
            
            if (availableCounties.length === 0) {
                const counties = [...new Set(response.data.map(c => c.county))];
                setAvailableCounties(counties);
            }
        } catch (err) {
            console.error('Error fetching county performance:', err);
        }
    };

    const fetchPerformanceAnalytics = async () => {
        try {
            const [throughputRes, processingRes] = await Promise.all([
                apiAdmin.post('/tpm-analytics/throughput-metrics', {
                    filter: getFilterObject,
                    interval: timeInterval
                }),
                apiAdmin.post('/tpm-analytics/processing-time-stats', {
                    filter: getFilterObject,
                    groupBy: groupByDimension
                })
            ]);
            
            setThroughputMetrics(throughputRes.data);
            setProcessingTimeStats(processingRes.data);
        } catch (err) {
            console.error('Error fetching performance analytics:', err);
        }
    };

    const fetchTokenAnalytics = async () => {
        try {
            const [trendsRes, costRes] = await Promise.all([
                apiAdmin.post('/tpm-analytics/token-trends', {
                    filter: getFilterObject,
                    interval: timeInterval
                }),
                apiAdmin.post('/tpm-analytics/cost-per-document', {
                    filter: getFilterObject,
                    groupBy: groupByDimension,
                    promptTokenRate: 2.50,
                    completionTokenRate: 10
                })
            ]);
            
            setTokenTrends(trendsRes.data);
            setCostAnalysis(costRes.data);
        } catch (err) {
            console.error('Error fetching token analytics:', err);
        }
    };

    const fetchDocumentCategoryMetricsAnalytics = async () => {
        try {
            const response = await apiAdmin.post('/tpm-analytics/document-distribution', getFilterObject);
            
            setDocumentDistribution(response.data);            
            if (availableCategories.length === 0) {
                const types = [...new Set(response.data.map(d => d.category))];
                setAvailableCategories(types);
            }
        } catch (err) {
            console.error('Error fetching document type metrics analytics:', err);
        }
    };

    const fetchBatchAnalytics = async () => {
        try {
            const response = await apiAdmin.post('/tpm-analytics/batch-duration', getFilterObject);
            setBatchDurations(response.data);
        } catch (err) {
            console.error('Error fetching batch analytics:', err);
        }
    };

    const loadAnalytics = async () => {
        setLoading(true);
        
        try {
            await Promise.all([
                fetchDashboardSummary(),
                fetchCountyPerformance(),
                fetchPerformanceAnalytics(),
                fetchTokenAnalytics(),
                fetchDocumentCategoryMetricsAnalytics(),
                fetchBatchAnalytics()
            ]);
        } catch (err) {
            console.error('Error loading analytics:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApplyFilters = () => {
        loadAnalytics();
    };

    const handleResetFilters = () => {
        setDateRange({
            startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0]
        });
        setSelectedCounties([]);
        setSelectedCategories([]);
        
        loadAnalytics();
    };

    const formatNumber = (num) => {
        if (!num && num !== 0) return '0';
        return num.toLocaleString();
    };


    const formatDuration = (seconds) => {
        if (!seconds) return '0s';
        if (seconds < 60) return `${seconds.toFixed(1)}s`;
        if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
        return `${(seconds / 3600).toFixed(1)}h`;
    };

    const formatDurationMinutesSeconds = (seconds) => {
        if (!seconds) return '0:00';
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const renderSummaryCards = () => {
        if (!dashboardSummary) {
            return (
                <Grid container spacing={3} className="mb-4 summary-cards-container">
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                            <Typography color="textSecondary">
                                {loading ? 'Loading dashboard summary...' : 'No data available'}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            );
        }

        const totalCost = costAnalysis.reduce((sum, item) => sum + (item.estimatedCost || 0), 0);
        const avgCostPerDoc = dashboardSummary.totalDocuments > 0 ? totalCost / dashboardSummary.totalDocuments : 0;

        const cards = [
            {
                title: 'Total Documents',
                value: formatNumber(dashboardSummary.totalDocuments),
                subtitle: `${dashboardSummary.dateRangeStart ? new Date(dashboardSummary.dateRangeStart).toLocaleDateString() : ''} - ${dashboardSummary.dateRangeEnd ? new Date(dashboardSummary.dateRangeEnd).toLocaleDateString() : ''}`,
                icon: <MdDataUsage size={48} />,
                color: COLORS.info,
                borderClass: 'info',
                size: 'grow'
            },
            {
                title: 'Total Estimated Cost',
                value: FormatMoney(totalCost),
                subtitle: `Avg ${FormatMoney(avgCostPerDoc)} per doc`,
                icon: <MdAttachMoney size={48} />,
                color: COLORS.accent,
                borderClass: 'accent',
                size: 'grow'
            },
            {
                title: 'Success Rate',
                value: `${dashboardSummary.successRate?.toFixed(2) || 0}%`,
                subtitle: `${formatNumber(dashboardSummary.successfulDocs)} successful`,
                icon: <MdCheckCircle size={48} />,
                color: COLORS.success,
                borderClass: 'success',
                progress: dashboardSummary.successRate || 0,
                size: 'grow'
            },
            {
                title: 'Skip Rate',
                value: `${dashboardSummary.skipRate?.toFixed(2) || 0}%`,
                subtitle: `${formatNumber(dashboardSummary.skippedDocs)} skipped`,
                icon: <MdWarning size={48} />,
                color: COLORS.warning,
                borderClass: 'warning',
                progress: dashboardSummary.skipRate || 0,
                size: 'grow'
            },
            {
                title: 'Total Tokens Used',
                value: formatNumber(dashboardSummary.totalTokens),
                subtitle: `Avg ${formatNumber((dashboardSummary.totalTokens/dashboardSummary.totalDocuments).toFixed(0))} per doc`,
                icon: <MdSpeed size={48} />,
                color: COLORS.primary,
                borderClass: 'primary',
                size: 'grow'
            },
            {
                title: 'Avg Processing Time',
                value: formatDuration(dashboardSummary.averageProcessingTime),
                subtitle: 'Per document',
                icon: <MdAccessTime size={48} />,
                color: COLORS.secondary,
                borderClass: 'secondary',
                size: 'grow'
            }
        ];

        const renderCard = (card, index) => (
            <Grid size={card.size} key={index}>
                <Card className={`summary-card ${card.borderClass}`}>
                    <CardContent>
                        <Box display="flex" flexDirection="column" height="100%">
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                <Box flex={1}>
                                    <Typography color="textSecondary" gutterBottom variant="body2">
                                        {card.title}
                                    </Typography>
                                    <Typography variant="h4" className="metric-value">
                                        {card.value}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {card.subtitle}
                                    </Typography>
                                </Box>
                                <Box sx={{ color: card.color }}>
                                    {card.icon}
                                </Box>
                            </Box>
                            <Box mt="auto" sx={{ height: card.progress !== undefined ? 'auto' : '6px' }}>
                                {card.progress !== undefined ? (
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={Math.min(card.progress, 100)} 
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            backgroundColor: 'rgba(0,0,0,0.1)',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: card.color
                                            }
                                        }}
                                    />
                                ) : (
                                    <Box sx={{ height: 6 }} />
                                )}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        );

        return (
            <>
                <Grid container spacing={1} className="mb-2 summary-cards-container">
                    {cards.slice(0, 3).map(renderCard)}
                </Grid>
                <Grid container spacing={1} className="mb-4 summary-cards-container">
                    {cards.slice(3).map((card, index) => renderCard(card, index + 3))}
                </Grid>
            </>
        );
    };

    const renderFiltersPanel = () => (
        <Accordion className="mb-4">
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center">
                    <MdFilterList size={24} className="me-2" />
                    <Typography variant="h6">Filters</Typography>
                    {(selectedCounties.length > 0 || selectedCategories.length > 0) && (
                        <Badge 
                            badgeContent={selectedCounties.length +  selectedCategories.length} 
                            color="primary" 
                            className="ms-2"
                        />
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>
                    <Grid size="auto">
                        <TextField
                            label="Start Date"
                            type="date"
                            value={dateRange.startDate}
                            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 150 }}
                            size="small"
                        />
                    </Grid>
                    <Grid size="auto">
                        <TextField
                            label="End Date"
                            type="date"
                            value={dateRange.endDate}
                            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 150 }}
                            size="small"
                        />
                    </Grid>
                    <Grid size="auto">
                        <FormControl sx={{ minWidth: 200, maxWidth: 220 }} size="small">
                            <InputLabel id="counties-label">Counties</InputLabel>
                            <Select
                                labelId="counties-label"
                                multiple
                                value={selectedCounties}
                                onChange={(e) => setSelectedCounties(e.target.value)}
                                label="Counties"
                                renderValue={(selected) => {
                                    if (selected.length === 0) return '';
                                    if (selected.length > 2) {
                                        return (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                <Chip 
                                                    label={`${selected.length} counties selected`} 
                                                    size="small" 
                                                />
                                            </Box>
                                        );
                                    }
                                    return (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} size="small" />
                                            ))}
                                        </Box>
                                    );
                                }}
                            >
                                {availableCounties.map((county) => (
                                    <MenuItem key={county} value={county}>{county}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size="auto">
                        <FormControl sx={{ minWidth: 220, maxWidth: 220 }} size="small">
                            <InputLabel id="categories-label">Document Categories</InputLabel>
                            <Select
                                labelId="categories-label"
                                multiple
                                value={selectedCategories}
                                onChange={(e) => setSelectedCategories(e.target.value)}
                                label="Document Categories"
                                renderValue={(selected) => {
                                    if (selected.length === 0) return '';
                                    if (selected.length > 2) {
                                        return (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                <Chip 
                                                    label={`${selected.length} categories selected`} 
                                                    size="small" 
                                                />
                                            </Box>
                                        );
                                    }
                                    return (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} size="small" />
                                            ))}
                                        </Box>
                                    );
                                }}
                            >
                                {availableCategories.map((type) => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size="auto">
                        <FormControl sx={{ minWidth: 150 }} size="small">
                            <InputLabel id="time-interval-label">Time Interval</InputLabel>
                            <Select
                                labelId="time-interval-label"
                                value={timeInterval}
                                onChange={(e) => setTimeInterval(e.target.value)}
                                label="Time Interval"
                            >
                                <MenuItem value="Hour">Hourly</MenuItem>
                                <MenuItem value="Day">Daily</MenuItem>
                                <MenuItem value="Week">Weekly</MenuItem>
                                <MenuItem value="Month">Monthly</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size="auto">
                        <FormControl sx={{ minWidth: 180 }} size="small">
                            <InputLabel id="group-by-label">Group By</InputLabel>
                            <Select
                                labelId="group-by-label"
                                value={groupByDimension}
                                onChange={(e) => setGroupByDimension(e.target.value)}
                                label="Group By"
                            >
                                <MenuItem value="County">County</MenuItem>
                                <MenuItem value="InstrumentType">Instrument Type</MenuItem>
                                <MenuItem value="Category">Category</MenuItem>
                                <MenuItem value="Status">Status</MenuItem>
                                <MenuItem value="Batch">Batch</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size="auto">
                        <Box display="flex" gap={2} alignItems="center" height="40px">
                            <Button 
                                variant="contained" 
                                onClick={handleApplyFilters}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <MdRefresh />}
                                size="small"
                            >
                                Apply Filters
                            </Button>
                            <Button 
                                variant="outlined" 
                                onClick={handleResetFilters}
                                disabled={loading}
                                size="small"
                            >
                                Reset
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );

    const renderOverviewTab = () => (
        <Grid container spacing={3}>
            <Grid size={12}>
                <Card className="chart-card">
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Document Processing Throughput
                        </Typography>
                        {throughputMetrics && throughputMetrics.length > 0 ? (
                            <ResponsiveContainer width="100%" height={350}>
                                <AreaChart data={throughputMetrics}>
                                    <defs>
                                        <linearGradient id="colorDocs" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8}/>
                                            <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="timePeriod" 
                                        tickFormatter={(value) => new Date(value).toLocaleDateString()}
                                    />
                                    <YAxis />
                                    <RechartsTooltip 
                                        labelFormatter={(value) => new Date(value).toLocaleString()}
                                    />
                                    <Legend />
                                    <Area 
                                        type="monotone" 
                                        dataKey="documentCount" 
                                        stroke={COLORS.primary} 
                                        fillOpacity={1}
                                        fill="url(#colorDocs)"
                                        name="Documents Processed"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height={350}>
                                <Typography color="textSecondary">No data available</Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid size={12}>
                <Card className="chart-card">
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            County Success Rates
                        </Typography>
                        {countyPerformance && countyPerformance.length > 0 ? (
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={countyPerformance.slice(0, 5)} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" domain={[0, 100]} />
                                    <YAxis dataKey="county" type="category" width={100} />
                                    <RechartsTooltip 
                                        formatter={(value, name) => {
                                            if (name === 'Success Rate %') {
                                                return `${value.toFixed(2)}%`;
                                            }
                                            return value;
                                        }}
                                    />
                                    <Bar dataKey="successRate" fill={COLORS.success} name="Success Rate %" />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height={350}>
                                <Typography color="textSecondary">No data available</Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid size={12}>
                <Card className="chart-card">
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Document Category Distribution
                        </Typography>
                        {documentDistribution && documentDistribution.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={documentDistribution.slice(0, 6)}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ category, percentage }) => 
                                            `${category}: ${percentage.toFixed(2)}%`
                                        }
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="count"
                                    >
                                        {documentDistribution.slice(0, 6).map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index % Object.values(COLORS).length]} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                                <Typography color="textSecondary">No data available</Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const renderPerformanceTab = () => (
        <Grid container spacing={3}>
            <Grid size={12}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Processing Time Statistics by {groupByDimension}
                        </Typography>
                        {processingTimeStats && processingTimeStats.length > 0 ? (
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart data={processingTimeStats}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="groupName" />
                                    <YAxis label={{ value: 'Seconds', angle: -90, position: 'insideLeft' }} />
                                    <RechartsTooltip 
                                        formatter={(value) => value.toFixed(2)}
                                    />
                                    <Legend />
                                    <Bar dataKey="minDuration" fill={COLORS.success} name="Min" />
                                    <Bar dataKey="averageDuration" fill={COLORS.info} name="Average" />
                                    <Bar dataKey="maxDuration" fill={COLORS.error} name="Max" />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                                <Typography color="textSecondary">No data available</Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={12}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Recent Batch Processing Times
                        </Typography>
                        {batchDurations && batchDurations.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart 
                                    data={[...batchDurations].sort((a, b) => new Date(a.startTime || a.batchId) - new Date(b.startTime || b.batchId))}
                                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                >
                                    <defs>
                                        <linearGradient id="colorBatchDuration" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8}/>
                                            <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="batchId" 
                                        label={{ value: 'Batch', position: 'insideBottom', offset: -5 }}
                                        tickFormatter={(value) => `#${value}`}
                                    />
                                    <YAxis 
                                        label={{ value: 'Duration (mm:ss)', angle: -90, position: 'insideBottomLeft' }}
                                        tickFormatter={(value) => formatDurationMinutesSeconds(value)}
                                    />
                                    <RechartsTooltip 
                                        formatter={(value, name) => {
                                            if (name === 'Duration') {
                                                return formatDurationMinutesSeconds(value);
                                            }
                                            return value;
                                        }}
                                        labelFormatter={(value) => `Batch #${value}`}
                                    />
                                    <Legend />
                                    <Area 
                                        type="monotone" 
                                        dataKey="duration" 
                                        stroke={COLORS.primary}
                                        strokeWidth={2}
                                        fillOpacity={1}
                                        fill="url(#colorBatchDuration)"
                                        name="Duration"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                                <Typography color="textSecondary">No data available</Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const renderTokensTab = () => (
        <Grid container spacing={3}>
            <Grid size={12}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Token Usage Over Time
                        </Typography>
                        {tokenTrends && tokenTrends.length > 0 ? (
                            <ResponsiveContainer width="100%" height={350}>
                                <AreaChart data={tokenTrends} margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                                    <defs>
                                        <linearGradient id="colorTokens" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={COLORS.secondary} stopOpacity={0.8}/>
                                            <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="timePeriod" 
                                        tickFormatter={(value) => new Date(value).toLocaleDateString()}
                                    />
                                    <YAxis width={80} />
                                    <RechartsTooltip 
                                        labelFormatter={(value) => new Date(value).toLocaleDateString()}
                                    />
                                    <Legend />
                                    <Area 
                                        type="monotone" 
                                        dataKey="totalTokens" 
                                        stroke={COLORS.secondary} 
                                        fillOpacity={1}
                                        fill="url(#colorTokens)"
                                        name="Total Tokens Used"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height={350}>
                                <Typography color="textSecondary">No data available</Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid size={12}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Cost Analysis
                        </Typography>
                        {costAnalysis && costAnalysis.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={costAnalysis}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="groupName" />
                                    <YAxis />
                                    <RechartsTooltip 
                                        formatter={(value, name) => {
                                            if (name === 'Cost ($)') {
                                                return `$${value.toFixed(2)}`;
                                            }
                                            return value;
                                        }}
                                    />
                                    <Legend />
                                    <Bar dataKey="estimatedCost" fill={COLORS.accent} name="Cost ($)" />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                                <Typography color="textSecondary">No data available</Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const renderDocumentTypeMetricsTab = () => {
        const groupedData = documentDistribution.reduce((acc, doc) => {
            const category = doc.category || doc.Category || 'Unknown';
            if (!acc[category]) {
                acc[category] = {
                    category,
                    totalCount: 0,
                    totalTokens: 0,
                    totalProcessingTime: 0,
                    percentage: 0
                };
            }
            
            acc[category].totalCount += doc.count || 0;
            acc[category].totalTokens += (doc.avgTokens || 0) * (doc.count || 0);
            acc[category].totalProcessingTime += (doc.avgProcessingTime || 0) * (doc.count || 0);
            acc[category].percentage += doc.percentage || 0;
            
            return acc;
        }, {});

        const rows = Object.values(groupedData)
            .sort((a, b) => b.totalCount - a.totalCount)
            .map((group, index) => {
                const avgTokens = group.totalCount > 0 ? group.totalTokens / group.totalCount : 0;
                const avgProcessingTime = group.totalCount > 0 ? group.totalProcessingTime / group.totalCount : 0;
                
                return {
                    id: `category-${index}`,
                    category: group.category,
                    count: group.totalCount,
                    percentage: group.percentage,
                    avgTokens,
                    avgProcessingTime
                };
            });
       
        const columns = [
            { 
                field: 'category', 
                headerName: 'Category', 
                flex: 1.5,
                minWidth: 150,
                renderCell: (params) => (
                    <Box sx={{ fontWeight: 'medium' }}>
                        {params.value}
                    </Box>
                )
            },
            { 
                field: 'count', 
                headerName: 'Count', 
                flex: 0.8,
                minWidth: 100,
                valueFormatter: (value) => formatNumber(value)
            },
            { 
                field: 'percentage', 
                headerName: 'Percentage', 
                flex: 1.2,
                minWidth: 150,
                renderCell: (params) => (
                    <Box display="flex" alignItems="center" width="100%">
                        <Box sx={{ minWidth: 50 }}>
                            {(params.value || 0).toFixed(2)}%
                        </Box>
                        <LinearProgress 
                            variant="determinate" 
                            value={Math.min(params.value || 0, 100)}
                            sx={{ 
                                flexGrow: 1, 
                                ml: 2, 
                                height: 8, 
                                borderRadius: 3,
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: COLORS.primary
                                }
                            }}
                        />
                    </Box>
                )
            },
            { 
                field: 'avgTokens', 
                headerName: 'Avg Tokens', 
                flex: 0.8,
                minWidth: 100,
                valueFormatter: (value) => formatNumber(value)
            },
            { 
                field: 'avgProcessingTime', 
                headerName: 'Avg Processing Time', 
                flex: 1,
                minWidth: 140,
                valueFormatter: (value) => formatDuration(value)
            }
        ];

        return (
            <Grid container spacing={3}>
                <Grid size={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Document Category Performance Metrics
                            </Typography>
                            {documentDistribution && documentDistribution.length > 0 ? (
                                <Box sx={{ height: 600, width: '100%' }}>
                                    <DataGrid
                                        rows={rows}
                                        columns={columns}
                                        initialState={{
                                            pagination: {
                                                paginationModel: { pageSize: 25 }
                                            }
                                        }}
                                        pageSizeOptions={[10, 25, 50, 100]}
                                        disableRowSelectionOnClick
                                        disableColumnMenu
                                        sx={{
                                            '& .MuiDataGrid-row:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                            },
                                            '& .MuiDataGrid-columnHeader': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                fontWeight: 'bold'
                                            }
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                                    <Typography color="textSecondary">No data available</Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };
       
    return (
        <div>
            <title>TPM Document Indexing Analytics</title>
            <Box p={3}>
                <Typography variant="h4" gutterBottom>
                    TPM Document Indexing Analytics
                </Typography>
                
                {renderFiltersPanel()}

                {renderSummaryCards()}

                <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" className="mb-4">
                    <Tab label="Overview" />
                    <Tab label="Performance" />
                    <Tab label="Tokens & Costs" />
                    <Tab label="Category Metrics" />
                </Tabs>

                {activeTab === 0 && renderOverviewTab()}
                {activeTab === 1 && renderPerformanceTab()}
                {activeTab === 2 && renderTokensTab()}
                {activeTab === 3 && renderDocumentTypeMetricsTab()}

                <Box mt={4} textAlign="center" color="text.secondary" fontSize="body2.fontSize">
                    <Typography variant="caption">
                        Data updated as of {new Date(dashboardSummary?.lastUpdated || Date.now()).toLocaleString()}
                    </Typography>
                </Box>
            </Box>
        </div>
    );
};

export default TpmAnalytics;
